import { useMemo, useState, useCallback } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ReactGrid } from '@silevis/reactgrid';
import '@silevis/reactgrid/styles.css';
import DownloadIcon from '@mui/icons-material/Download';

const VisitsTableTab = ({ visitsTable = [] }) => {
    const { t: translate } = useTranslation();

    const normalizedData = useMemo(() => {
        if (Array.isArray(visitsTable)) return visitsTable;
        if (typeof visitsTable === 'object' && visitsTable !== null) {
            // Convert object to array if needed
            return Object.entries(visitsTable).map(([key, value]) => ({
                key,
                ...value,
            }));
        }
        return [];
    }, [visitsTable]);

    const hasData = normalizedData.length > 0;

    // Define columns explicitly based on the actual data structure
    const columnDefinitions = useMemo(() => {
        return [
            { key: 'contractId', label: 'Contract ID', width: 120 },
            { key: 'code', label: 'Code', width: 120 },
            { key: 'totalVisits', label: 'Total Visits', width: 120 },
            { key: 'uniqueVisits', label: 'Unique Visits', width: 130 },
            { key: 'avgNavigationTime', label: 'Avg Nav Time (s)', width: 150 },
            { key: 'totalMoneyConverted', label: 'Money Converted', width: 150 },
            { key: 'converted', label: 'Converted', width: 100 },
            { key: 'conversionRate', label: 'Conversion Rate (%)', width: 160 },
        ];
    }, []);

    // State for column widths (resizable columns)
    const [columns, setColumns] = useState(
        columnDefinitions.map((col) => ({
            columnId: col.key,
            width: col.width,
            resizable: true,
        }))
    );

    // Handle column resize
    const handleColumnResize = useCallback((columnId, width) => {
        setColumns((prevColumns) => prevColumns.map((col) => (col.columnId === columnId ? { ...col, width } : col)));
    }, []);

    // CSV Export function
    const exportToCSV = useCallback(() => {
        if (!hasData) return;

        // Define headers
        const headers = columnDefinitions.map((col) => col.label);

        // Convert data to CSV rows
        const csvRows = normalizedData.map((row) => {
            return columnDefinitions
                .map((col) => {
                    const value = row[col.key];
                    let displayValue = value ?? '';

                    // Format numbers appropriately
                    if (col.key === 'totalMoneyConverted' && typeof value === 'number') {
                        displayValue = value.toFixed(2);
                    } else if (col.key === 'conversionRate' && typeof value === 'number') {
                        displayValue = value.toFixed(2);
                    } else if (col.key === 'avgNavigationTime' && typeof value === 'number') {
                        displayValue = value;
                    }

                    // Escape quotes and wrap in quotes if contains comma or newline
                    const stringValue = String(displayValue);
                    if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
                        return `"${stringValue.replace(/"/g, '""')}"`;
                    }
                    return stringValue;
                })
                .join(',');
        });

        // Add totals row
        const totalsRow = columnDefinitions
            .map((col) => {
                let totalValue = '';

                if (col.key === 'contractId') {
                    totalValue = 'TOTALS';
                } else if (col.key === 'code') {
                    totalValue = '';
                } else if (col.key === 'totalVisits') {
                    totalValue = normalizedData.reduce((acc, row) => acc + (Number(row.totalVisits) || 0), 0);
                } else if (col.key === 'uniqueVisits') {
                    totalValue = normalizedData.reduce((acc, row) => acc + (Number(row.uniqueVisits) || 0), 0);
                } else if (col.key === 'avgNavigationTime') {
                    const validRows = normalizedData.filter((row) => typeof row.avgNavigationTime === 'number' && !isNaN(row.avgNavigationTime));
                    if (validRows.length > 0) {
                        const avg = validRows.reduce((acc, row) => acc + row.avgNavigationTime, 0) / validRows.length;
                        totalValue = avg.toFixed(2);
                    } else {
                        totalValue = '0';
                    }
                } else if (col.key === 'totalMoneyConverted') {
                    const sum = normalizedData.reduce((acc, row) => acc + (Number(row.totalMoneyConverted) || 0), 0);
                    totalValue = sum.toFixed(2);
                } else if (col.key === 'converted') {
                    totalValue = normalizedData.reduce((acc, row) => acc + (Number(row.converted) || 0), 0);
                } else if (col.key === 'conversionRate') {
                    const validRows = normalizedData.filter((row) => typeof row.conversionRate === 'number' && !isNaN(row.conversionRate));
                    if (validRows.length > 0) {
                        const avg = validRows.reduce((acc, row) => acc + row.conversionRate, 0) / validRows.length;
                        totalValue = avg.toFixed(2);
                    } else {
                        totalValue = '0';
                    }
                }

                const stringValue = String(totalValue);
                if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
                    return `"${stringValue.replace(/"/g, '""')}"`;
                }
                return stringValue;
            })
            .join(',');

        // Combine headers and rows
        const csvContent = [headers.join(','), ...csvRows, totalsRow].join('\n');

        // Create blob and download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `traffic-visits-table-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, [hasData, normalizedData, columnDefinitions]);

    const headerRow = useMemo(() => {
        return {
            rowId: 'header',
            cells: columnDefinitions.map((col) => ({
                type: 'header',
                text: translate(`traffic.visitsTable.${col.key}`) || col.label,
            })),
        };
    }, [columnDefinitions, translate]);

    const rows = useMemo(() => {
        if (!hasData) return [headerRow];

        const dataRows = normalizedData.map((row, idx) => {
            return {
                rowId: idx,
                cells: columnDefinitions.map((col) => {
                    const value = row[col.key];
                    let displayValue = value ?? '';

                    // Format numbers appropriately
                    if (col.key === 'totalMoneyConverted' && typeof value === 'number') {
                        displayValue = `$${value.toFixed(2)}`;
                    } else if (col.key === 'conversionRate' && typeof value === 'number') {
                        displayValue = `${value.toFixed(2)}%`;
                    } else if (col.key === 'avgNavigationTime' && typeof value === 'number') {
                        displayValue = `${value}s`;
                    }

                    return {
                        type: 'text',
                        text: String(displayValue),
                    };
                }),
            };
        });

        // Calculate totals row
        const totalsRow = {
            rowId: 'totals',
            cells: columnDefinitions.map((col) => {
                let totalValue = '';

                if (col.key === 'contractId') {
                    totalValue = 'TOTALS';
                } else if (col.key === 'code') {
                    totalValue = '';
                } else if (col.key === 'totalVisits') {
                    const sum = normalizedData.reduce((acc, row) => acc + (Number(row.totalVisits) || 0), 0);
                    totalValue = String(sum);
                } else if (col.key === 'uniqueVisits') {
                    const sum = normalizedData.reduce((acc, row) => acc + (Number(row.uniqueVisits) || 0), 0);
                    totalValue = String(sum);
                } else if (col.key === 'avgNavigationTime') {
                    const validRows = normalizedData.filter((row) => typeof row.avgNavigationTime === 'number' && !isNaN(row.avgNavigationTime));
                    if (validRows.length > 0) {
                        const avg = validRows.reduce((acc, row) => acc + row.avgNavigationTime, 0) / validRows.length;
                        totalValue = `${avg.toFixed(2)}s`;
                    } else {
                        totalValue = '0s';
                    }
                } else if (col.key === 'totalMoneyConverted') {
                    const sum = normalizedData.reduce((acc, row) => acc + (Number(row.totalMoneyConverted) || 0), 0);
                    totalValue = `$${sum.toFixed(2)}`;
                } else if (col.key === 'converted') {
                    const sum = normalizedData.reduce((acc, row) => acc + (Number(row.converted) || 0), 0);
                    totalValue = String(sum);
                } else if (col.key === 'conversionRate') {
                    const validRows = normalizedData.filter((row) => typeof row.conversionRate === 'number' && !isNaN(row.conversionRate));
                    if (validRows.length > 0) {
                        const avg = validRows.reduce((acc, row) => acc + row.conversionRate, 0) / validRows.length;
                        totalValue = `${avg.toFixed(2)}%`;
                    } else {
                        totalValue = '0%';
                    }
                }

                return {
                    type: 'text',
                    text: String(totalValue),
                };
            }),
        };

        return [headerRow, ...dataRows, totalsRow];
    }, [hasData, normalizedData, headerRow, columnDefinitions]);

    if (!hasData) {
        return (
            <Box
                sx={{
                    width: '100%',
                    minHeight: '240px',
                    border: '1px dashed #d1d1d1',
                    borderRadius: '8px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '24px',
                    gap: 1,
                }}>
                <Typography variant="h6" fontWeight="bold" sx={{ color: '#6f6f6f' }}>
                    {translate('traffic.visitsTable_tab') || 'Visits Table'}
                </Typography>
                <Typography sx={{ color: '#9a9a9a' }}>{translate('traffic.visitsTable_empty') || 'No visits table data available'}</Typography>
            </Box>
        );
    }

    return (
        <Box display="flex" flexDirection="column" gap={2} width="100%">
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" fontWeight="bold">
                    {translate('traffic.visitsTable_tab') || 'Visits Table'} ({normalizedData.length})
                </Typography>
                <Button variant="contained" color="primary" startIcon={<DownloadIcon />} onClick={exportToCSV} disabled={!hasData}>
                    {translate('traffic.export_csv') || 'Export to CSV'}
                </Button>
            </Box>

            <Box
                sx={{
                    width: '100%',
                    height: 'calc(100vh - 300px)',
                    overflow: 'auto',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    '& .rg-root': {
                        '--rg-cell-border-color': '#e0e0e0',
                        '--rg-header-background-color': '#f5f5f5',
                        '--rg-selection-border-color': '#1976d2',
                        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    },
                }}>
                <ReactGrid
                    rows={rows}
                    columns={columns}
                    onColumnResized={handleColumnResize}
                    enableRangeSelection
                    enableRowSelection
                    enableColumnSelection
                    stickyTopRows={1}
                />
            </Box>
        </Box>
    );
};

VisitsTableTab.displayName = 'VisitsTableTab';

export default VisitsTableTab;
